import { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  RefreshCw,
  Download,
  Edit,
  Copy,
  Trash2,
  Lock,
  Search,
  Filter,
  FileText,
  Send,
  DollarSign,
} from 'lucide-react';
import { ASNDocument, InvoiceDocument, Document, DocumentFilters, DocumentSort, DocumentStatus } from '../types/document';
import {
  fetchDocuments,
  fetchDocumentStats,
  deleteDocument,
  duplicateASNDocument,
  duplicateInvoiceDocument,
  bulkDeleteDocuments,
  bulkUpdateASNStatus,
} from '../utils/documents';
import { supabase } from '../lib/supabase';
import { exportToJSON } from '../utils/export';
import { eventBus } from '../utils/eventBus';
import { fetchActiveTradingPartners } from '../utils/tradingPartners';
import type { InjectedTradingPartner, TradingPartner } from '../types/tradingPartner';

type DocumentType = 'asn' | 'invoice';

interface DocumentsListProps {
  onCreateNew: (type: DocumentType) => void;
  onEditDocument: (type: DocumentType, documentId: string) => void;
  /** Initial tab — synced from App's documentType so returning from a form lands on the correct tab. */
  initialDocumentType?: DocumentType;
  /**
   * Trading partners injected by the host app via the web-component
   * `trading-partners` attribute. Used to resolve `trading_partner_id`
   * UUIDs to human-readable partner names without an extra round-trip
   * per row. Optional — when absent (standalone mode) we still resolve
   * via the editor DB list.
   */
  injectedTPs?: InjectedTradingPartner[];
}

const statusColors: Record<DocumentStatus, string> = {
  draft: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  valid: 'bg-blue-100 text-blue-800 border-blue-300',
  sent: 'bg-green-100 text-green-800 border-green-300',
  failed: 'bg-red-100 text-red-800 border-red-300',
  in_progress: 'bg-orange-100 text-orange-800 border-orange-300',
};

const statusLabels: Record<DocumentStatus, string> = {
  draft: 'Draft',
  valid: 'Valid',
  sent: 'Sent',
  failed: 'Failed',
  in_progress: 'In Progress',
};

interface ColumnConfig {
  header: string;
  className?: string;
  render: (doc: Document) => React.ReactNode;
}

export function DocumentsList({ onCreateNew, onEditDocument, initialDocumentType, injectedTPs }: DocumentsListProps) {
  const [documentType, setDocumentType] = useState<DocumentType>(initialDocumentType || 'asn');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, draft: 0, valid: 0, sent: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<DocumentFilters>({});
  const [sort, setSort] = useState<DocumentSort>({ field: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [dbTradingPartners, setDbTradingPartners] = useState<TradingPartner[]>([]);
  const itemsPerPage = 50;

  useEffect(() => {
    loadDocuments();
  }, [filters, sort, documentType, currentPage]);

  // WDEV-115: Load the editor-side TP list once so we can map each
  // document's trading_partner_id (UUID) → trading_partners.code, which
  // we then resolve to a human-readable name using the host-injected
  // partner list. Failures are logged but non-fatal: rows fall back to
  // "Unknown partner (#code)" rather than crashing the whole table.
  useEffect(() => {
    let cancelled = false;
    fetchActiveTradingPartners()
      .then((tps) => {
        if (!cancelled) setDbTradingPartners(tps);
      })
      .catch((err) => {
        console.error('[DocumentsList] Failed to load trading partners for name resolution:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Map of document `trading_partner_id` (UUID) → display info derived
   * from the host-injected `trading-partners` payload. Built once per
   * render — partner counts are small (typically <100 even for large
   * tenants) so a flat Map is cheaper than a per-row search.
   *
   * Resolution order, per FK row:
   *   1. UUID → editor `trading_partners.code` (deterministic FK in editor DB)
   *   2. `code` → injected partner `name` (deterministic key per CLAUDE.md)
   *   3. If injected list is absent (standalone mode), fall back to the
   *      editor's own `trading_partners.name`.
   *   4. If neither lookup yields a name, emit `Unknown partner (#code)`
   *      at render time — never an empty string or silent default.
   */
  const tradingPartnerLookup = useMemo(() => {
    const injectedByCode = new Map<string, string>();
    for (const tp of injectedTPs ?? []) {
      injectedByCode.set(String(tp.code), tp.name);
    }

    const byUuid = new Map<string, { code: string; name?: string }>();
    for (const tp of dbTradingPartners) {
      const injectedName = injectedByCode.get(tp.code);
      // Prefer the host-provided name (the user-facing label) over the
      // editor DB name; fall back to the editor DB name in standalone
      // mode (no injected list, e.g. dev/testing).
      const resolvedName = injectedName ?? (injectedTPs ? undefined : tp.name);
      byUuid.set(tp.id, { code: tp.code, name: resolvedName });
    }
    return byUuid;
  }, [dbTradingPartners, injectedTPs]);

  /**
   * Resolve a document's trading_partner_id to a display label.
   * Never returns an empty or silently-defaulted string — when the
   * partner can't be resolved we surface that explicitly so the
   * operator knows the row needs attention.
   */
  const resolveTradingPartnerLabel = (doc: Document): string => {
    if (!doc.trading_partner_id) {
      // Document has no FK at all — likely a legacy row pre-template-junction.
      return doc.trading_partner_name || 'Unknown partner (#?)';
    }
    const entry = tradingPartnerLookup.get(doc.trading_partner_id);
    if (entry?.name) return entry.name;
    // Last-resort fallback for cards from the joined embed (server-side
    // path that may yet return a name even when our client lookup misses).
    if (doc.trading_partner_name) return doc.trading_partner_name;
    const codeLabel = entry?.code ?? '?';
    return `Unknown partner (#${codeLabel})`;
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const [{ data, count }, newStats] = await Promise.all([
        fetchDocuments(documentType, { ...filters, page: currentPage, pageSize: itemsPerPage }, sort),
        fetchDocumentStats(documentType),
      ]);
      setDocuments(data);
      setTotalCount(count);
      setStats(newStats);
      // Reset selection when data changes significantly (e.g. type switch)
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.size === documents.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(documents.map((doc) => doc.id)));
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this document?')) {
      try {
        await deleteDocument(id);

        eventBus.emit('document:deleted', {
          type: documentType,
          documentId: id,
        });

        await loadDocuments();
      } catch (error) {
        alert('Failed to delete document');
      }
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const { getCustomerId } = await import('../lib/config');
      const customerId = getCustomerId();
      if (!customerId) throw new Error('No customer ID found');

      if (documentType === 'asn') {
        await duplicateASNDocument(id, customerId);
      } else {
        await duplicateInvoiceDocument(id, customerId);
      }
      await loadDocuments();
    } catch (error) {
      console.error('Failed to duplicate document:', error);
      alert(`Failed to duplicate document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleBulkDelete = async () => {
    if (confirm(`Delete ${selectedIds.size} document(s)?`)) {
      try {
        await bulkDeleteDocuments(Array.from(selectedIds));
        setSelectedIds(new Set());
        await loadDocuments();
      } catch (error) {
        alert('Failed to delete documents');
      }
    }
  };

  const handleBulkExport = async () => {
    const validDocs = documents.filter(
      (doc) => selectedIds.has(doc.id) && (doc.status === 'valid' || doc.status === 'draft')
    );

    if (validDocs.length === 0) {
      alert('No valid documents selected for export');
      return;
    }

    try {
      const errors: string[] = [];

      for (const doc of validDocs) {
        const typedDoc = documentType === 'asn'
          ? (doc as ASNDocument)
          : (doc as InvoiceDocument);

        if (!typedDoc.data) {
          errors.push(`Document ${doc.id}: missing form data — cannot send.`);
          continue;
        }

        if (!doc.trading_partner_id) {
          errors.push(`Document ${doc.id}: no trading partner assigned. Re-create the document.`);
          continue;
        }

        const jsonData = JSON.parse(exportToJSON(typedDoc.data));
        if (typedDoc.data.tradingPartner) {
          jsonData.tradingPartner = typedDoc.data.tradingPartner;
        }

        // Look up the TP by its UUID (direct FK — deterministic, no ambiguity)
        const { data: tp, error: tpError } = await supabase
          .from('trading_partners')
          .select('id, code, name')
          .eq('id', doc.trading_partner_id)
          .maybeSingle();

        if (tpError) {
          errors.push(`Document ${doc.id}: failed to fetch trading partner: ${tpError.message}`);
          continue;
        }
        if (!tp) {
          errors.push(`Document ${doc.id}: trading partner ${doc.trading_partner_id} not found in database.`);
          continue;
        }
        if (!tp.code) {
          errors.push(`Document ${doc.id}: trading partner "${tp.name}" has no code (reports partner ID). Cannot link to reports system.`);
          continue;
        }

        console.log('[editor] Send TP resolution:', {
          docId: doc.id,
          docTradingPartnerId: doc.trading_partner_id,
          tpId: tp.id,
          tpCode: tp.code,
          tpName: tp.name,
          parsedCode: parseInt(tp.code, 10),
        });

        const externalId = parseInt(tp.code, 10);
        if (isNaN(externalId)) {
          errors.push(`Document ${doc.id}: trading partner "${tp.name}" has invalid code "${tp.code}". Must be a numeric reports partner ID.`);
          continue;
        }

        const tpName = typedDoc.data.tradingPartner || tp.name;

        eventBus.emit('document:sent', {
          type: documentType,
          documentId: doc.id,
          data: jsonData,
          tradingPartnerCode: externalId,
          tradingPartnerName: tpName,
        });
      }

      if (errors.length > 0) {
        console.error('[editor] Send errors:', errors);
        alert(`Some documents could not be sent:\n\n${errors.join('\n')}`);
        if (errors.length === validDocs.length) return; // all failed
      }

      const sentIds = validDocs
        .filter((doc) => doc.trading_partner_id != null)
        .map((d) => d.id);
      if (sentIds.length > 0) {
        await bulkUpdateASNStatus(sentIds, 'sent');
      }
      await loadDocuments();
      setSelectedIds(new Set());
      const sentCount = validDocs.length - errors.length;
      if (sentCount > 0) {
        alert(`Sent ${sentCount} document(s)` + (errors.length > 0 ? `, ${errors.length} failed.` : '.'));
      }
    } catch (error) {
      alert('Failed to export documents');
    }
  };

  const handleExportCSV = () => {
    const csvHeader = documentType === 'asn'
      ? ['Doc Type', 'Reference ID', 'Trading Partner', 'Status', 'Created', 'Updated']
      : ['Doc Type', 'Invoice Number', 'Trading Partner', 'Status', 'Created', 'Updated'];

    const csvData = documents.map((doc) => {
      const refId = documentType === 'asn'
        ? (doc as ASNDocument).shipment_id
        : (doc as InvoiceDocument).invoice_number;

      return [
        doc.document_type,
        refId,
        resolveTradingPartnerLabel(doc),
        statusLabels[doc.status as DocumentStatus] || doc.status,
        new Date(doc.created_at).toLocaleDateString(),
        new Date(doc.updated_at).toLocaleDateString(),
      ];
    });

    const csv = [
      csvHeader.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${documentType}-documents-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const columns = useMemo<ColumnConfig[]>(() => {
    const commonColumns: ColumnConfig[] = [
      {
        header: 'Created',
        render: (doc) => new Date(doc.created_at).toLocaleDateString(),
        className: 'text-gray-600'
      },
      {
        header: 'Modified',
        render: (doc) => new Date(doc.updated_at).toLocaleDateString(),
        className: 'text-gray-600'
      },
      {
        header: 'Trading Partner',
        render: (doc) => resolveTradingPartnerLabel(doc),
        className: 'text-gray-600'
      },
      {
        header: 'Status',
        render: (doc) => (
          <span
            className={`px-2 py-1 text-xs font-medium rounded border ${statusColors[doc.status]}`}
            data-testid="status-badge"
          >
            {statusLabels[doc.status]}
          </span>
        )
      }

    ];

    if (documentType === 'asn') {
      return [
        {
          header: 'Shipment ID',
          render: (doc) => (doc as ASNDocument).shipment_id,
          className: 'font-medium text-gray-900'
        },
        ...commonColumns,
        {
          header: 'Total Pieces',
          render: (doc) => (doc as ASNDocument).total_pieces?.toLocaleString() || '-',
          className: 'text-right text-gray-900'
        },
        {
          header: 'Total Weight',
          render: (doc) => `${(doc as ASNDocument).total_weight?.toLocaleString()} LB` || '-',
          className: 'text-right text-gray-900'
        }
      ];
    } else if (documentType === 'invoice') {
      return [
        {
          header: 'Invoice Number',
          render: (doc) => (doc as InvoiceDocument).invoice_number,
          className: 'font-medium text-gray-900'
        },
        {
          header: 'Invoice Date',
          render: (doc) => (doc as InvoiceDocument).invoice_date || '-',
          className: 'text-gray-600'
        },
        {
          header: 'PO Number',
          render: (doc) => (doc as InvoiceDocument).data.purchaseOrderNumber || '-',
          className: 'text-gray-600'
        },
        ...commonColumns,
        {
          header: 'Line Items',
          render: (doc) => (doc as InvoiceDocument).data.details?.length || '-',
          className: 'text-right text-gray-900'
        },
        {
          header: 'Invoice Total',
          render: (doc) => `$${(doc as InvoiceDocument).data.summary?.invoiceTotal}` || '-',
          className: 'text-right text-gray-900 font-medium'
        }
      ];
    }
    
    return commonColumns;
    // tradingPartnerLookup participates because resolveTradingPartnerLabel
    // closes over it — including it ensures the Trading Partner column
    // re-renders once the TP list finishes loading.
  }, [documentType, tradingPartnerLookup]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="documents-list">
      <div className="mx-auto px-4 py-6">
        <div className="mb-6 flex gap-2 border-b border-gray-200">
          <button
            onClick={() => {
              setDocumentType('asn');
              setCurrentPage(1);
            }}
            className={`px-6 py-3 font-medium transition-colors ${documentType === 'asn'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            ASN Documents
          </button>
          <button
            onClick={() => {
              setDocumentType('invoice');
              setCurrentPage(1);
            }}
            className={`px-6 py-3 font-medium transition-colors ${documentType === 'invoice'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
              }`}
          >
            <DollarSign className="w-4 h-4 inline mr-2" />
            Invoices
          </button>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="card-compact p-4">
            <div className="text-xs text-gray-600">Total Documents</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
          </div>
          <div className="card-compact p-4">
            <div className="text-xs text-gray-600">Drafts</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">{stats.draft}</div>
          </div>
          <div className="card-compact p-4">
            <div className="text-xs text-gray-600">Valid/Ready</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{stats.valid}</div>
          </div>
          <div className="card-compact p-4">
            <div className="text-xs text-gray-600">Sent</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.sent}</div>
          </div>
        </div>

        <div className="card-compact mb-4">
          <div className="p-4 border-b border-gray-200">
            <div className="flex gap-3 flex-wrap items-center">
              <button onClick={() => onCreateNew(documentType)} className="btn-primary px-4 py-2 text-sm">
                <Plus size={16} className="inline mr-1.5" />
                {documentType === 'asn' ? 'New ASN' : 'New Invoice'}
              </button>
              <button onClick={loadDocuments} className="btn-secondary px-4 py-2 text-sm" disabled={loading}>
                <RefreshCw size={16} className={`inline mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`btn-secondary px-4 py-2 text-sm ${showFilters ? 'bg-gray-100' : ''}`}
              >
                <Filter size={16} className="inline mr-1.5" />
                Filters
              </button>
              <button onClick={handleExportCSV} className="btn-secondary px-4 py-2 text-sm">
                <Download size={16} className="inline mr-1.5" />
                Export CSV
              </button>

              {selectedIds.size > 0 && (
                <>
                  <div className="h-6 w-px bg-gray-300" />
                  <span className="text-sm text-gray-600">{selectedIds.size} selected</span>
                  <button onClick={handleBulkExport} className="btn-success px-3 py-2 text-sm">
                    <Send size={16} className="inline mr-1.5" />
                    Send Selected
                  </button>
                  <button onClick={handleBulkDelete} className="btn-danger px-3 py-2 text-sm">
                    <Trash2 size={16} className="inline mr-1.5" />
                    Delete Selected
                  </button>
                </>
              )}

              <div className="ml-auto flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={filters.searchQuery || ''}
                    onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                    className="input-compact pl-9 pr-3 border border-gray-300 rounded-md w-64"
                  />
                </div>
              </div>
            </div>

            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-4 gap-3">
                <div>
                  <label className="label-compact block">Status</label>
                  <select
                    value={filters.status || ''}
                    onChange={(e) =>
                      setFilters({ ...filters, status: e.target.value as DocumentStatus | undefined })
                    }
                    className="input-compact w-full border border-gray-300 rounded-md"
                  >
                    <option value="">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="valid">Valid</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                    <option value="in_progress">In Progress</option>
                  </select>
                </div>
                <div>
                  <label className="label-compact block">From Date</label>
                  <input
                    type="date"
                    value={filters.dateFrom || ''}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    className="input-compact w-full border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="label-compact block">To Date</label>
                  <input
                    type="date"
                    value={filters.dateTo || ''}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    className="input-compact w-full border border-gray-300 rounded-md"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => setFilters({})}
                    className="btn-secondary px-3 py-1.5 text-sm w-full"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === documents.length && documents.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  {columns.map((col, idx) => (
                    <th key={idx} className={`px-4 py-3 text-left font-medium text-gray-700 ${col.className || ''}`}>
                      {col.header}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading ? (
                  <tr>
                    <td colSpan={columns.length + 2} className="px-4 py-8 text-center text-gray-500">
                      Loading documents...
                    </td>
                  </tr>
                ) : documents.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 2} className="px-4 py-8 text-center">
                      {documentType === 'asn' ? (
                        <FileText className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                      ) : (
                        <DollarSign className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                      )}
                      <p className="text-gray-500">No {documentType === 'asn' ? 'ASNs' : 'invoices'} found</p>
                      <button onClick={() => onCreateNew(documentType)} className="text-blue-600 hover:text-blue-700 text-sm mt-2">
                        Create your first {documentType === 'asn' ? 'ASN' : 'invoice'}
                      </button>
                    </td>
                  </tr>
                ) : (
                  documents.map((doc) => (
                    <tr
                      key={doc.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      data-testid="document-row"
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(doc.id)}
                          onChange={() => handleSelectOne(doc.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-gray-300"
                        />
                      </td>
                      {columns.map((col, idx) => (
                        <td key={idx} className={`px-4 py-3 ${col.className || ''}`}>
                          {col.render(doc)}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-2 justify-end">
                          {doc.status === 'sent' ? (
                            <span
                              className="text-gray-400 p-1 cursor-not-allowed"
                              title="Sent documents cannot be modified. Use Copy to create a new draft."
                            >
                              <Lock size={16} />
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!doc.trading_partner_id) {
                                  alert('Error: This document has no Trading Partner assigned. Data is inconsistent. Please contact support.');
                                  return;
                                }
                                onEditDocument(documentType, doc.id);
                              }}
                              className="text-blue-600 hover:text-blue-700 p-1"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicate(doc.id);
                            }}
                            className="text-gray-600 hover:text-gray-700 p-1"
                            title={doc.status === 'sent' ? 'Copy to new draft' : 'Duplicate'}
                          >
                            <Copy size={16} />
                          </button>
                          {doc.status !== 'sent' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(doc.id);
                              }}
                              className="text-red-600 hover:text-red-700 p-1"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="btn-secondary px-3 py-1.5 text-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
